import * as React from "react";
import { useCallback, useMemo } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/lib/shadcn/ui/dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { Badge } from "@/lib/shadcn/ui/badge";
import { Skeleton } from "@/lib/shadcn/ui/skeleton";
import { XCircle } from "lucide-react";
import { Platform } from "@/models";
import { ReservationVersion } from "@/models/responses";
import { useGetReservationHistoryQuery } from "@/store/hosthub-api";

interface ReservationHistoryDialogProps {
    open: boolean;
    /** Logical reservation id whose version timeline to fetch. */
    reservationId: number | null;
    /** Reservation code, shown in the header. */
    reservationCode: string;
    onClose: () => void;
}

/** A single displayed field with a human label and a formatter. */
export interface FieldSpec {
    key: keyof ReservationVersion;
    label: string;
    format: (v: unknown) => string;
}

const euro = (v: unknown): string => {
    if (v === undefined || v === null) return "—";
    const n = typeof v === "number" ? v : Number(v);
    return `€${n.toFixed(2)}`;
};

const text = (v: unknown): string => {
    if (v === undefined || v === null || v === "") return "—";
    return String(v);
};

const date = (v: unknown): string => {
    if (!v) return "—";
    const d = v instanceof Date ? v : new Date(v as string);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
};

/** Localized date+time, e.g. "08/06/2026, 14:27". Guards empty/invalid → "—". */
const dateTime = (v: unknown): string => {
    if (!v) return "—";
    const d = v instanceof Date ? v : new Date(v as string);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-GB");
};

/** Plain integer/count, e.g. nights or number of guests. Guards empty → "—". */
const count = (v: unknown): string => {
    if (v === undefined || v === null || v === "") return "—";
    const n = typeof v === "number" ? v : Number(v);
    return Number.isNaN(n) ? "—" : String(n);
};

/** Boolean → "Yes"/"No" (used for the cancellation flag). */
const bool = (v: unknown): string => (v ? "Yes" : "No");

/** Platform id → human name ("Airbnb"/"Booking"/"Out"), mirroring the digest. */
const platform = (v: unknown): string => {
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isNaN(n)) return "—";
    return Platform[n] ?? `Platform ${n}`;
};

/**
 * AUDITED_FIELDS is the canonical audited field set the history timeline diffs
 * on. Each entry pairs a `ReservationVersion` key with a human label and a
 * formatter. TRACKED_FIELDS extends it with `platform` (see below).
 *
 * Covers: the channel-owned/booking fields the sync gates a modification on
 * (income, commission, dates, guest, guests, nights), the full monetary
 * breakdown (vat, sustainability fee, temporary-stay duty, cleaning fee/cost,
 * net income, net/gross ADR, payout, management fee), and the cancellation flag.
 */
export const AUDITED_FIELDS: FieldSpec[] = [
    { key: "receivableIncome", label: "income", format: euro },
    { key: "commission", label: "commission", format: euro },
    { key: "vat", label: "VAT", format: euro },
    { key: "sustainabilityFee", label: "sustainability fee", format: euro },
    { key: "temporaryStayDuty", label: "temporary-stay duty", format: euro },
    { key: "cleaningFee", label: "cleaning fee", format: euro },
    { key: "cleaningCost", label: "cleaning cost", format: euro },
    { key: "netIncome", label: "net income", format: euro },
    { key: "netAdr", label: "net ADR", format: euro },
    { key: "grossAdr", label: "gross ADR", format: euro },
    { key: "payout", label: "payout", format: euro },
    { key: "managementFee", label: "management fee", format: euro },
    { key: "bookingDate", label: "booked-on", format: date },
    { key: "startDate", label: "check-in", format: date },
    { key: "endDate", label: "check-out", format: date },
    { key: "guestName", label: "guest", format: text },
    { key: "numberOfGuests", label: "guests", format: count },
    { key: "actualDaysOfStay", label: "nights", format: count },
    { key: "isCancelled", label: "cancelled", format: bool },
];

/**
 * TRACKED_FIELDS is the history dialog's per-version display/diff set: the
 * canonical audited fields PLUS `platform`, which the timeline has always
 * surfaced (a channel switch is a real, operator-meaningful modification).
 */
export const TRACKED_FIELDS: FieldSpec[] = [
    ...AUDITED_FIELDS,
    { key: "platform", label: "platform", format: platform },
];

export interface FieldDiff {
    label: string;
    from: string;
    to: string;
}

/**
 * Compute the field-level diff of `current` vs the older `previous` version over
 * the given `fields` set. Defaults to the history dialog's TRACKED_FIELDS (which
 * includes `platform`).
 */
export function diffVersions(
    current: ReservationVersion,
    previous: ReservationVersion | undefined,
    fields: FieldSpec[] = TRACKED_FIELDS,
): FieldDiff[] {
    if (!previous) return [];
    const diffs: FieldDiff[] = [];
    for (const spec of fields) {
        const from = spec.format(previous[spec.key]);
        const to = spec.format(current[spec.key]);
        if (from !== to) {
            diffs.push({ label: spec.label, from, to });
        }
    }
    return diffs;
}

type Marker = "created" | "manual" | "cancelled" | "update" | "recompute" | "adopt" | "manual_revert";

/**
 * Determine the badge for a version, keyed on the per-version `changeSource`
 * (the reason this version was written) plus whether it's the earliest version
 * and whether it represents a cancellation. NOT keyed on `origin` (per-reservation
 * provenance), which mislabelled internal recomputes as "HostHub sync" (AS-7).
 * Missing/legacy `changeSource` is treated as `manual`.
 */
export function markerFor(version: ReservationVersion, isEarliest: boolean): { kind: Marker; label: string } {
    if (version.isCancelled) {
        return { kind: "cancelled", label: "Cancelled" };
    }
    const source = version.changeSource || "manual";
    switch (source) {
        case "manual_revert":
            return { kind: "manual_revert", label: "Reverted to manual" };
        case "recompute":
            return { kind: "recompute", label: "Recalculated" };
        case "adopt":
            return { kind: "adopt", label: "Linked to HostHub" };
        case "hosthub_event":
            return isEarliest
                ? { kind: "created", label: "Booking created" }
                : { kind: "update", label: "HostHub sync" };
        case "manual":
        default:
            return isEarliest
                ? { kind: "manual", label: "Imported (manual)" }
                : { kind: "manual", label: "Manual edit" };
    }
}

const HistorySkeleton: React.FC = () => (
    <div data-testid="history-skeleton" className="space-y-4" aria-hidden="true">
        {[0, 1, 2].map((i) => (
            <div key={i} className="space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-64" />
                <Skeleton className="h-3 w-52" />
            </div>
        ))}
    </div>
);

/**
 * ReservationHistoryDialog (ui-spec Screen 2) — read-only version timeline for
 * one logical reservation, newest first, with a per-version field diff vs the
 * previous version. The latest entry is tagged "Current"; cancellation entries
 * carry a red "Cancelled" chip. Loading skeleton + error/retry; focus-trap and
 * Escape/backdrop close are inherited from the shadcn Dialog.
 */
export const ReservationHistoryDialog: React.FC<ReservationHistoryDialogProps> = ({
    open,
    reservationId,
    reservationCode,
    onClose,
}) => {
    const { data, isFetching, isError, refetch } = useGetReservationHistoryQuery(reservationId ?? 0, {
        skip: !open || reservationId === null,
    });

    const handleRetry = useCallback(() => {
        void refetch();
    }, [refetch]);

    // Newest-first: sort by versionId descending so the timeline order is
    // deterministic regardless of how the backend returns the rows.
    const versions = useMemo(() => {
        const list = data?.history ?? [];
        return [...list].sort((a, b) => b.versionId - a.versionId);
    }, [data]);

    const handleOpenChange = useCallback(
        (next: boolean) => {
            if (!next) onClose();
        },
        [onClose],
    );

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                data-testid="reservation-history-dialog"
                className="max-w-[95vw] sm:max-w-2xl max-h-[85vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle>History — {reservationCode}</DialogTitle>
                    <DialogDescription className="sr-only">
                        Read-only version timeline for reservation {reservationCode}, newest first, with a per-version
                        field diff.
                    </DialogDescription>
                </DialogHeader>

                {isFetching && <HistorySkeleton />}

                {!isFetching && isError && (
                    <div
                        data-testid="history-error-panel"
                        role="alert"
                        className="flex flex-col items-center gap-4 py-8 text-center"
                    >
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100 text-red-600">
                            <XCircle className="h-6 w-6" aria-hidden="true" />
                        </div>
                        <p className="max-w-md text-sm text-slate-700">
                            The reservation history could not be loaded.
                        </p>
                        <div className="flex gap-2">
                            <Button data-testid="history-retry" onClick={handleRetry}>
                                Retry
                            </Button>
                            <Button variant="outline" onClick={onClose}>
                                Close
                            </Button>
                        </div>
                    </div>
                )}

                {!isFetching && !isError && (
                    <ol data-testid="history-timeline" className="space-y-4">
                        {versions.map((version, index) => {
                            const isLatest = index === 0;
                            const isEarliest = index === versions.length - 1;
                            const previous = versions[index + 1];
                            const diffs = diffVersions(version, previous);
                            const marker = markerFor(version, isEarliest);
                            return (
                                <li
                                    key={version.versionId}
                                    data-testid={`history-version-${version.versionId}`}
                                    className="rounded-md border p-3"
                                >
                                    <div className="flex flex-wrap items-center gap-2">
                                        {marker.kind === "cancelled" ? (
                                            <Badge
                                                data-testid="history-cancelled-chip"
                                                className="border-transparent bg-red-700 text-white"
                                            >
                                                {marker.label}
                                            </Badge>
                                        ) : (
                                            <Badge variant="secondary">{marker.label}</Badge>
                                        )}
                                        {isLatest && (
                                            <Badge data-testid="history-current-chip" variant="outline">
                                                Current
                                            </Badge>
                                        )}
                                        {marker.kind === "cancelled" && (
                                            <span
                                                data-testid="history-cancelled-at"
                                                className="text-xs text-red-700"
                                            >
                                                Cancelled {dateTime(version.cancelledAt)}
                                            </span>
                                        )}
                                    </div>

                                    <p
                                        data-testid="history-version-timestamp"
                                        className="mt-1 text-xs text-slate-500"
                                    >
                                        {isEarliest ? "Created" : "Updated"} {dateTime(version.versionCreatedAt)}
                                    </p>

                                    {version.changeSource === "manual" && version.changeReason && (
                                        <p
                                            data-testid="history-change-reason"
                                            className="mt-2 rounded-md bg-slate-50 px-2 py-1 text-sm italic text-slate-700"
                                        >
                                            &ldquo;{version.changeReason}&rdquo;
                                        </p>
                                    )}

                                    {isEarliest ? (
                                        <p className="mt-2 text-sm text-slate-600">
                                            Initial snapshot: {version.actualDaysOfStay} nights ·{" "}
                                            {euro(version.receivableIncome)} · {text(version.guestName)}
                                        </p>
                                    ) : diffs.length > 0 ? (
                                        <ul className="mt-2 space-y-1 text-sm">
                                            {diffs.map((d) => (
                                                <li key={d.label} data-testid={`history-diff-${d.label}`}>
                                                    <span className="font-medium">{d.label}</span>:{" "}
                                                    <span className="text-slate-500">{d.from}</span>
                                                    {" → "}
                                                    <span className="text-slate-900">{d.to}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="mt-2 text-sm text-slate-500">No tracked fields changed.</p>
                                    )}
                                </li>
                            );
                        })}
                    </ol>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ReservationHistoryDialog;
